import React, { FC, Fragment, useEffect, useState } from 'react';

import { Col, Row } from 'antd';
import { config as appConfig } from 'config/config';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { getDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { ChallengeDetailsBox } from 'src/components/challenge/ChallengeDetailsBox';
import ChallengeResourceWrapper from 'src/components/challenge/ChallengeResourceWrapper';
import { ExperienceDetailWrapper } from 'src/components/challenge/ExperienceDetailWrapper';
import Quiz from 'src/components/quiz/Quiz';
import { IRootReducers } from 'src/reducers';
import { ButtonStatusEnum, ChallengeTypeEnum } from 'src/shared/enums';
import {
  IChallengeQuiz,
  IChallengeResourceRender,
  IExperienceDetailWrapper,
} from 'src/shared/models';
import { setQuizBehavior } from 'utils';

interface OwnProps extends IChallengeResourceRender {
  config: IChallengeQuiz;
  challengeType: number;
  icon?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const ChallengeQuiz: FC<Props> = ({
  challengeType,
  config,
  resourceBehavior,
  updateUserPoints,
  updateChallenge,
  icon,
}): JSX.Element => {
  // !Deprecated: Challenge behaviour are handler into ChallengeDetails.tsx
  const isTraining = challengeType === ChallengeTypeEnum.FORMATION;
  const isExperience = challengeType === ChallengeTypeEnum.EXPERIENCE;

  const [quizConfig, setQuizConfig] = useState<IChallengeQuiz>(
    isTraining ? config : setQuizBehavior(config, resourceBehavior)
  );

  const { description, points, title, showPresentation, allowRetry } =
    quizConfig;
  const [showQuiz, setShowQuiz] = useState<boolean>(!showPresentation);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const isEventQuiz = showPresentation;

  const handleGetStatistics = async (idChallenge: number) => {
    const { GET_QUIZ_STATISTICS } = apiPaths.CHALLENGES;

    const request = `${GET_QUIZ_STATISTICS}/${idChallenge}`;

    try {
      const response: {
        // Data has others properties that are not required.
        data: {
          userAnswers: [
            {
              resourceM2MList: [
                {
                  idResource: {
                    idResource: number;
                    indResource: number;
                  };
                }
              ];
            }
          ];
        };
      } = await getDataCall({
        dataPath: request,
        callConfig: {},
      });

      const _config = { ...quizConfig };
      const _map: {
        [idQuestion: number]: {
          index: number;
          answers: {
            [idAnswer: number]: number;
          };
        };
      } = {};

      // Build questions maps
      _config.questions.forEach(
        ({ idResource: idQuestion, answers }, questionIndex) => {
          _map[idQuestion] = {
            index: questionIndex,
            answers: {},
          };
          answers.forEach((answer, answerIndex) => {
            const { idResource: idAnswer } = answer;
            _map[idQuestion].answers[idAnswer] = answerIndex;
          });
        }
      );

      // Set users responses
      response.data.userAnswers.forEach((userAnswers) => {
        ++_config.totalResponses;
        userAnswers.resourceM2MList.forEach((userResponse) => {
          const { idResource: idAnswer, indResource: idQuestion } =
            userResponse.idResource;

          const questionIndex = _map[idQuestion].index;
          const answerIndex = _map[idQuestion].answers[idAnswer];

          ++_config.questions[questionIndex].answers[answerIndex].userResponses;
        });
      });

      setQuizConfig(_config);
    } catch (error) {
      console.error(error);
    }
  };

  const handleShowQuiz = () => setShowQuiz(isEventQuiz ? false : allowRetry);

  useEffect(() => {
    const { showStatistics, idChallenge } = quizConfig;

    if (showStatistics) {
      setIsLoading(true);
      handleGetStatistics(idChallenge).then(() => {
        setIsLoading(false);
      });
    } else {
      setQuizConfig(quizConfig);
    }
  }, [quizConfig.isQuizDone]);

  const quizComponent = (): JSX.Element =>
    showQuiz && (
      <Quiz
        config={quizConfig}
        handleShowQuiz={handleShowQuiz}
        {...{ updateUserPoints, updateChallenge }}
      />
    );

  const isChallengeTraining = challengeType === ChallengeTypeEnum.FORMATION;

  if (!quizConfig?.shouldRender) return null;

  if (!isExperience)
    return (
      <ChallengeResourceWrapper
        styleClass="challengeDetail--Quiz"
        title={formatMessage({
          id: 'page.challenge.quiz.title',
        })}
        icon={!isChallengeTraining && icon}
      >
        <Row
          justify="center"
          className={`challengeDetail--quiz ${
            isChallengeTraining ? 'challengeDetail--campaign-quiz' : 'old-quiz'
          }`}
          id="challengeDetail--quiz"
        >
          <Col span={24}>
            {showPresentation && (
              <ChallengeDetailsBox
                allowRetry={allowRetry}
                completed={quizConfig.isQuizDone}
                description={description}
                disabled={quizConfig.disabled}
                image={
                  quizConfig.urlImage ??
                  `${appConfig.APP.BASE_URL}/delegate_back.svg`
                }
                loading={isLoading}
                points={points}
                setShowComponent={setShowQuiz}
                showQuiz={showQuiz}
                title={title}
              />
            )}
            {quizComponent()}
          </Col>
        </Row>
      </ChallengeResourceWrapper>
    );

  const wrapperConfig: IExperienceDetailWrapper = {
    title,
    label: config.quizLabel,
    button: {
      status: ButtonStatusEnum.SOON,
      label: formatMessage({
        id: 'experience.button-soon',
      }),
    },
  };

  return (
    <ExperienceDetailWrapper {...wrapperConfig}>
      <Fragment />
    </ExperienceDetailWrapper>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challengeType: state.app.challenge?.challengeType,
  };
};

export default connect(mapStateToProps)(ChallengeQuiz);
